/**
 * 全局音频管理器 - 单例模式
 * 用于在 Login 和 SignUp 页面之间共享音频播放状态
 */

class AudioManager {
  constructor() {
    this.audio = null;
    this.isInitialized = false;
    this.listeners = new Set(); // 存储所有监听器（组件）
    this.fadeInInterval = null;
    this.hasStartedFlag = false; // 跟踪是否曾经播放过
  }

  // 初始化音频（只初始化一次）
  init() {
    if (this.isInitialized) return;

    this.audio = new Audio('/audio/dawn-by-sappheiros.mp3');
    this.audio.loop = true;
    this.audio.volume = 0; // 初始音量为 0，用于淡入效果
    this.isInitialized = true;
  }

  // 获取音频实例
  getAudio() {
    if (!this.isInitialized) {
      this.init();
    }
    return this.audio;
  }

  // 播放音频
  async play() {
    const audio = this.getAudio();
    if (!audio) return;

    try {
      await audio.play();
      this.hasStartedFlag = true; // 标记为已启动
      this.notifyListeners('play');
      this.fadeIn();
    } catch (error) {
      console.error('播放失败:', error);
      throw error;
    }
  }

  // 暂停音频
  pause() {
    const audio = this.getAudio();
    if (!audio) return;

    audio.pause();
    this.notifyListeners('pause');
  }

  // 切换播放/暂停
  async toggle() {
    const audio = this.getAudio();
    if (!audio) return;

    if (audio.paused) {
      await this.play();
    } else {
      this.pause();
    }
  }

  // 淡入效果
  fadeIn() {
    const audio = this.getAudio();
    if (!audio) return;

    // 清除之前的淡入动画（如果存在）
    if (this.fadeInInterval) {
      clearInterval(this.fadeInInterval);
    }

    const targetVolume = 0.2; // 20% 音量
    const duration = 2000; // 2 秒淡入
    const steps = 20;
    const stepTime = duration / steps;
    const volumeStep = targetVolume / steps;

    let currentStep = 0;
    const interval = setInterval(() => {
      currentStep++;
      audio.volume = Math.min(volumeStep * currentStep, targetVolume);

      if (currentStep >= steps) {
        clearInterval(interval);
        audio.volume = targetVolume;
        this.fadeInInterval = null;
      }
    }, stepTime);

    this.fadeInInterval = interval;
  }

  // 检查是否正在播放
  isPlaying() {
    const audio = this.getAudio();
    if (!audio) return false;
    return !audio.paused;
  }

  // 检查是否已启动（曾经播放过）
  hasStarted() {
    return this.hasStartedFlag;
  }

  // 添加监听器（组件注册）
  addListener(listener) {
    this.listeners.add(listener);
  }

  // 移除监听器（组件卸载）
  removeListener(listener) {
    this.listeners.delete(listener);
  }

  // 通知所有监听器状态变化
  notifyListeners(event) {
    this.listeners.forEach(listener => {
      if (typeof listener === 'function') {
        listener(event);
      }
    });
  }

  // 清理资源（页面卸载时调用）
  cleanup() {
    const audio = this.getAudio();
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
    }
    if (this.fadeInInterval) {
      clearInterval(this.fadeInInterval);
      this.fadeInInterval = null;
    }
  }
}

// 导出单例实例
export const audioManager = new AudioManager();
